import Navbar from "../../components/layout/Navbar";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { useState, FormEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GoogleLoginButton } from "../../components/GoogleAuth/GoogleAuth";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import supabase from "../../helper/supabaseClient";
import { selectGlobalData } from "../../features/appSlice";
import { OTPInput } from "input-otp";
import { setCredentials } from "../../features/authSlice";
import { getSites } from "../StorePages/UserStores";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);
  const [searchParams] = useSearchParams();
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
    color: "",
  });
  const { countries, userLocation } = useSelector(selectGlobalData);
  const [countryCode, setCountryCode] = useState("+234");

  const [step, setStep] = useState<"form" | "otp">("form");
  const [otpCode, setOtpCode] = useState("");
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    const nameFromUrl = searchParams.get("name");
    if (nameFromUrl) {
      localStorage.setItem("pendingStoreName", nameFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === "otp" && countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  const [formData, setFormData] = useState({
    full_name: "",
    // last_name: "",
    // gender: "male",
    phone: "",
    email: "",
    password1: "",
    password2: "",
    rememberMe: false,
    agreeToTerms: true,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (name === "password1") {
      checkPasswordStrength(value);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!validPhoneNumber) {
        toast.warning("Please enter a valid phone number.");
        return;
      }

      if (formData.password1 !== formData.password2) {
        toast.error("Passwords do not match");
        return;
      }

      const fullPhone = `${countryCode}${formData.phone}`;

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password1,
        options: {
          data: {
            full_name: formData.full_name,
            username: fullPhone,
          },
        },
      });

      if (error) throw error;

      if (data.user?.identities?.length === 0) {
        toast.error("An account with this email already exists.");
        return;
      }

      localStorage.setItem("verify-email", formData.email);
      toast.success("Registration successful!", {
        description: "Check your inbox to verify your email.",
      });
      window.fbq && window.fbq("track", "InitiateRegistration");

      setStep("otp");
      setCountdown(60);
    } catch (error: any) {
      toast.error(error.message || "Registration failed");
      console.error("Registration failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: formData.email,
        token: otpCode,
        type: "signup",
      });

      if (error) throw error;

      if (data?.session && data.user) {
        const userSites = await getSites(data.user.id);

        const nameParts = formData.full_name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName =
          nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

        const mappedUser = {
          id: data.user.id,
          email: data.user.email ?? "",
          first_name: data.user.user_metadata?.first_name ?? firstName,
          last_name: data.user.user_metadata?.last_name ?? lastName,
          last_login: new Date().toISOString(),
          sites: userSites?.data || [],
        };

        dispatch(
          setCredentials({
            user: mappedUser,
            token: data.session.access_token,
            refresh_token: data.session.refresh_token,
            rememberMe: formData.rememberMe,
          })
        );
      }

      toast.success("Account verified successfully!");
      navigate("/verify", { replace: true, state: { verified: true } });
    } catch (error: any) {
      console.warn("OTP verification failed:", error);
      toast.error(error.message || "Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: formData.email,
      });

      if (error) throw error;

      toast.success("New code sent!");
      setCountdown(60);
    } catch (error: any) {
      toast.error(error.message || "Failed to resend code");
    }
  };

  function checkPasswordStrength(password: string) {
    let score = 0; // Initialize score
    let feedback = "";

    // Define weights for different criteria
    const WEIGHTS = {
      length: 0.3, // Heavily weight length
      upperCase: 0.2, // Medium weight for uppercase
      lowerCase: 0.15, // Medium weight for lowercase
      numbers: 0.1, // Medium weight for numbers
      specialChars: 0.2, // Medium weight for special characters
      entropy: 0.2, // Additional entropy weight
    };

    // Criteria checks
    const length = password.length;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // Assign scores based on criteria
    if (length >= 8) score += (WEIGHTS.length * Math.min(16, length)) / 16; // Scale length up to 16 chars
    if (hasUpperCase) score += WEIGHTS.upperCase;
    if (hasLowerCase) score += WEIGHTS.lowerCase;
    if (hasNumbers) score += WEIGHTS.numbers;
    if (hasSpecialChars) score += WEIGHTS.specialChars;

    // Calculate entropy
    const uniqueChars = new Set(password).size;
    const entropy = Math.log2(uniqueChars) * length;
    if (entropy > 50) score += WEIGHTS.entropy;

    // Normalize score to 1
    score = Math.min(1, score);

    // Provide feedback based on the score
    if (score > 0.8) {
      feedback = "Strong password. Great job!";
    } else if (score >= 0.5) {
      feedback = "Moderate password. Consider adding special characters.";
    } else {
      feedback = "Weak password. Consider using more length and character variety.";
    }

    setPasswordStrength({
      score: Math.round(score * 100), // Return score as percentage
      feedback,
      color:
        score > 0.8
          ? "text-green-600"
          : score >= 0.5
            ? "text-orange-400"
            : "text-red-400",
    });
  }

  // Auto-detect user's dial code
  useEffect(() => {
    if (userLocation?.country && countries.length > 0) {
      const matched = countries.find((c) => c.name === userLocation.country);
      if (matched && matched.dialCode) {
        setCountryCode(matched.dialCode);
      }
    }
  }, [userLocation, countries]);

  const validatePhoneInput = (phone: string) => {
    const normalized = phone.replace(/\D/g, "");
    // Allow 7-15 digits
    const ok = normalized.length >= 7 && normalized.length <= 15;
    setValidPhoneNumber(ok);
    return ok;
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 sm:p-14 flex items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-start gap-20 ">
            {step === "form" ? (
              <div className="flex flex-col gap-2 items-start justify-start w-[500px]">
                <div>
                  <h2 className="">Create an account</h2>
                  <span>
                    Already have an account?{" "}
                    <Link to="/login">
                      <span className="underline text-[#7E57C2] font-medium">
                        Log in
                      </span>
                    </Link>
                  </span>
                </div>

                <div className="flex flex-col items-center mt-4 justify-center w-full">
                  <GoogleLoginButton />
                </div>
                <p className="text-center w-full">OR</p>

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col justify-center gap-8 w-full"
                >
                  <div className="flex items-start justify-center gap-6 flex-col w-full">
                    <div className="flex items-center justify-center gap-3 w-full">
                      <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                        <label htmlFor="firstName" className="text-[#666666CC]">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          id="firstName"
                          value={formData.full_name}
                          placeholder="John"
                          autoComplete="on"
                          onChange={handleChange}
                          className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                          required
                        />
                      </div>
                      {/* <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                        <label htmlFor="lastName" className="text-[#666666CC]">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          id="lastName"
                          placeholder="Doe"
                          autoComplete="on"
                          value={formData.last_name}
                          onChange={handleChange}
                          className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                          required
                        />
                      </div> */}
                    </div>
                    {/* <div className="flex items-start flex-col justify-center gap-2">
                      <span className="text-[#666666CC]">
                        What's your gender?
                      </span>
                      <div className="flex items-center justify-center gap-4">
                        <div className="flex items-center justify-center gap-1 w-full">
                          <input
                            type="radio"
                            name="gender"
                            id="female"
                            value="female"
                            checked={formData.gender === "female"}
                            onChange={handleChange}
                            className="accent-[#7E57C2]"
                          />
                          <label htmlFor="female">Female</label>
                        </div>
                        <div className="flex items-center justify-center gap-1 w-full">
                          <input
                            type="radio"
                            name="gender"
                            id="male"
                            value="male"
                            checked={formData.gender === "male"}
                            onChange={handleChange}
                            className="accent-[#7E57C2]"
                          />
                          <label htmlFor="male">Male</label>
                        </div>
                      </div>
                    </div> */}

                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="phoneNumber" className="text-[#666666CC]">
                        Phone Number
                      </label>
                      <div className="flex w-full gap-2">
                        <div className="relative w-[70px]">
                          <div className="absolute inset-0 w-full h-full border border-[#66666659] rounded-lg px-3 bg-white flex items-center text-sm z-10 pointer-events-none text-gray-700">
                            <span className="truncate">{countryCode}</span>
                            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
                              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </div>
                          <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
                          >
                            {countries.map((c) => (
                              <option key={c.dialCode} value={c.dialCode}>
                                {c.name} ({c.dialCode})
                              </option>
                            ))}
                          </select>
                        </div>

                        <input
                          type="tel"
                          name="phone"
                          id="phoneNumber"
                          placeholder="8012345678"
                          value={formData.phone}
                          onBlur={(e) => validatePhoneInput(e.target.value)}
                          onChange={(e) => {
                            // Only allow numbers
                            const val = e.target.value.replace(/\D/g, "");
                            handleChange({ target: { name: "phone", value: val } } as any);
                            validatePhoneInput(val);
                          }}
                          className="h-10 flex-1 border border-[#66666659] rounded-lg outline-0 px-3"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="email" className="text-[#666666CC]">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        placeholder="john@example.com"
                        autoComplete="on"
                        onChange={handleChange}
                        className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />
                    </div>

                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="password1" className="text-[#666666CC]">
                        Password
                      </label>
                      <div className="relative w-full">
                        <input
                          type={showPassword1 ? "text" : "password"}
                          name="password1"
                          id="password1"
                          value={formData.password1}
                          placeholder="••••••••"
                          onChange={handleChange}
                          className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword1((prev) => !prev)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                        >
                          {showPassword1 ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {formData.password1 && (
                        <p className={`text-sm ${passwordStrength.color}`}>
                          {passwordStrength.feedback}
                        </p>
                      )}
                    </div>

                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="password2" className="text-[#666666CC]">
                        Confirm Password
                      </label>
                      <div className="relative w-full">
                        <input
                          type={showPassword2 ? "text" : "password"}
                          name="password2"
                          id="password2"
                          value={formData.password2}
                          placeholder="••••••••"
                          onChange={handleChange}
                          className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword2((prev) => !prev)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                        >
                          {showPassword2 ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {formData.password2 && formData.password1 !== formData.password2 && (
                        <p className="password-strength-weak text-sm">
                          Passwords do not match!
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="w-full flex items-center justify-between">
                    <span className="hidden sm:block">
                      Already have an account?{" "}
                      <Link to="/login">
                        <span className=" underline text-[#7E57C2] font-medium">
                          Log in
                        </span>
                      </Link>
                    </span>
                    <Button
                      text={isLoading ? "Signing Up..." : "Sign Up"}
                      disabled={isLoading}
                      className="sm:w-fit w-full"
                    />
                  </div>
                </form>
              </div>
            ) : (
              <div className="flex flex-col gap-4 items-center justify-center w-[500px] text-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Verify your email
                  </h2>
                  <p className="text-gray-500 text-sm">
                    We've sent a 6-digit verification code to{" "}
                    <span className="font-semibold text-gray-700">
                      {formData.email}
                    </span>
                    .
                  </p>
                </div>

                <form
                  onSubmit={handleVerifyOtp}
                  className="w-full flex flex-col gap-6 mt-4 items-center"
                >
                  <div className="flex flex-col gap-2 text-left w-full items-center">
                    <label className="text-[#666666CC] text-sm self-start ml-4 sm:ml-8">
                      Verification Code
                    </label>
                    <OTPInput
                      maxLength={6}
                      value={otpCode}
                      onChange={setOtpCode}
                      render={({ slots }) => (
                        <div className="flex gap-2 sm:gap-3 justify-center w-full">
                          {slots.map((slot, index) => (
                            <div
                              key={index}
                              className={`relative flex h-12 w-10 sm:w-12 items-center justify-center border rounded-lg text-xl font-semibold transition-all ${
                                slot.isActive
                                  ? "border-[#7E57C2] ring-1 ring-[#7E57C2]"
                                  : "border-[#66666659]"
                              }`}
                            >
                              {slot.char !== null ? slot.char : ""}
                              {slot.hasFakeCaret && (
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                  <div className="h-6 w-px bg-black animate-pulse" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  </div>

                  <Button
                    text={isLoading ? "Verifying..." : "Verify Account"}
                    disabled={isLoading || otpCode.length !== 6}
                    className="w-[85%]"
                  />
                </form>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={countdown > 0}
                    className={`text-sm font-medium ${
                      countdown > 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-[#7E57C2] hover:underline"
                    }`}
                  >
                    {countdown > 0
                      ? `Resend code in ${countdown}s`
                      : "Didn't receive a code? Resend"}
                  </button>
                </div>
              </div>
            )}

            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%] items-center justify-end mt-1">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="/privacy_policy">
              <li>Privacy</li>
            </Link>
            <Link to="/terms_conditions">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
